import React, { Fragment } from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import hash from 'object-hash';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { map, groupBy, pipe, values, filter } from 'ramda';
import { sum, multiply } from 'utils/calculator';
import { legShape } from 'constants/propTypes';
import { marginTariff } from 'constants/tariffCodes';
import { mapObject } from 'utils/functions';
import { Column } from 'components/shared/Layout';
import LoyaltyPointsInfos from 'components/shared/LoyaltyPointsInfos';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import InventoryClassMessage from 'pages/BookingDetails/AfterSalesSummaryBox/InventoryClassMessage';
import RebookMessage from 'pages/BookingDetails/AfterSalesSummaryBox/RebookMessage';
import RefundMessage from 'pages/BookingDetails/AfterSalesSummaryBox/RefundMessage';
import productTypes from 'constants/productTypes';
import currencies from 'constants/currencies';
import PaxInfo from 'components/ui/BookingPathReviewBookingSummary/SegmentSummary/SegmentSummaryColumn/SegmentInfo/PaxInfo';
import { ANY_BELGIAN_STATION_SHORT_TARIFF_CODE, ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE } from 'constants/booking';
import { isNoMealClass, isNoMealRoute } from 'utils/meals';
import {
  getPaxDetails,
  getPaxSummary as getPaxInfoSummary,
  overrideCompanionFaresForABSADSAccessibleTravels
} from 'components/shared/JourneyDigest/JourneyDigest.utils';
import {
  countPaxTypes,
  groupObjectsInArrayByKeys,
  handlePaxInfo,
  handleTotalPoints
} from 'components/ui/BookingPathReviewBookingSummary/SegmentSummary/SegmentSummary.utils';

import { getPaxSummary, groupMeals, handleTotal } from '../../SegmentSummary.utils';

import { InfoLabel } from './Info/Info.styles';
import { StyledFareCondition, TravelDate, FareConditionsContainer, InfoGrid, InfoGridHeaderCell, InfoGridCell } from './SegmentInfo.styles';

const determineAddOnLegJourneyId = (isPsuedo) => (isPsuedo ? 'journeySummary.validBeforeJourney' : 'journeySummary.validAfterJourney');

const SegmentInfo = ({
  isAddOnLeg = false,
  leg: {
    direction,
    origin,
    destination,
    classOfService,
    trainNumber,
    bookingItems = [],
    meals = [],
    timing: { departs, arrives, isoDate },
    serviceType = {},
    extraProducts = [],
    seats
  },
  intl: { formatMessage },
  currency,
  trainMarginDeprecated,
  isMiceGroup
}) => {
  const passengers = bookingItems.map((item) => item.passenger);
  const groupedMeals = meals.length > 0 && groupMeals(meals);
  const firstBookingItem = bookingItems[0];
  const canLookupMessage = firstBookingItem && firstBookingItem?.canBeCancelled && firstBookingItem?.canBeRefunded;
  const canBeCancelled = firstBookingItem && firstBookingItem?.canBeCancelled;

  const paxDetails = bookingItems && getPaxDetails(passengers, bookingItems);
  const groupPaxInfosByType = groupObjectsInArrayByKeys(Object.values(getPaxInfoSummary(paxDetails)), 'type');
  const overrideAccesibleTravelPaxTypes = overrideCompanionFaresForABSADSAccessibleTravels(paxDetails, isAddOnLeg);
  const paxSummary = getPaxSummary(passengers, bookingItems);
  const paxCount = countPaxTypes(passengers);
  const paxInfo = paxCount && handlePaxInfo(paxDetails?.[0]?.productFamily?.name ?? '', paxCount);
  const fareInfos = paxInfo && paxInfo[0];
  const total = handleTotal(paxSummary);
  const totalPoints = handleTotalPoints(paxSummary);

  const departValue = isAddOnLeg
    ? `${origin?.name}: ${formatMessage({
        id: determineAddOnLegJourneyId(origin?.isPseudoStation)
      })}`
    : `${origin?.name} (${origin?.shortCode}) ${formatMessage({
        id: 'exchangeConfirmation.at'
      })} ${departs}`;

  const arriveValue = isAddOnLeg
    ? destination?.name
    : `${destination?.name} (${destination?.shortCode}) ${formatMessage({
        id: 'exchangeConfirmation.at'
      })} ${arrives}`;

  const trainMargin = extraProducts.reduce(
    (tot, { priceWithCurrency, code }) => (code === marginTariff.MARGIN_TRAIN ? tot + priceWithCurrency : tot),
    0
  );

  const combineDuplicateProductsWithQuantity = (p) => ({
    product: p[0],
    quantity: p.filter((product) => !product.cancelled).length
  });

  const extraProductsByPassengers = pipe(
    filter((i) => ![productTypes.DISABILITY_SUPPORT, productTypes.FOOD_AND_BEVERAGES].includes(i.type)),
    filter((i) => !i.isCancelled),
    filter(
      (epp) =>
        epp?.tariffDetails?.tariffCode?.slice(0, 3) !== ANY_BELGIAN_STATION_SHORT_TARIFF_CODE &&
        epp?.tariffDetails?.tariffCode?.slice(0, 3) !== ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE
    ),
    filter((i) => passengers.map((p) => p.id).includes(i.passenger?.id)),
    groupBy((p) => `${p.code}-${p.price}-${p.currency}`),
    values(),
    map(combineDuplicateProductsWithQuantity),
    map((p) => ({ product: p.product, quantity: p.quantity }))
  )(extraProducts);

  const extrasTotal = isAddOnLeg
    ? 0
    : extraProductsByPassengers.reduce((acc, { product: { price }, quantity }) => sum([acc, multiply(price, quantity)]), 0);

  const margin = trainMarginDeprecated > 0 ? trainMarginDeprecated : trainMargin;

  const getCoach = (pax) => {
    const seat = seats?.find((s) => s.passenger?.id === pax.id);

    return seat?.coach;
  };

  const getSeat = (pax) => {
    const seat = seats?.find((s) => s.passenger?.id === pax.id);

    return seat?.number;
  };

  const hasSeatingInfo = () => {
    const seatsFiltered = seats?.filter(
      (s) => (passengers.map((p) => p.id).includes(s.passenger?.id) && s.coach !== null) || s.number !== null
    );

    return seatsFiltered?.length > 0;
  };

  const shouldShowMeals =
    !isAddOnLeg && groupedMeals && !isNoMealClass(classOfService.code) && !isNoMealRoute(origin?.uic, destination?.uic);

  return (
    <>
      <Column>
        <TravelDate>{moment.utc(isoDate).format('Do MMM YYYY')}</TravelDate>
      </Column>
      <InfoGrid>
        <InfoGridHeaderCell>
          <FormattedMessage id="segmentInfo.depart" />:
        </InfoGridHeaderCell>
        <InfoGridCell data-testid="depart" column="span 3">
          {departValue}
        </InfoGridCell>
        <InfoGridHeaderCell>
          <FormattedMessage id="segmentInfo.arrive" />:
        </InfoGridHeaderCell>
        <InfoGridCell data-testid="arrives" column="span 3">
          {arriveValue}
        </InfoGridCell>
        <InfoGridHeaderCell>
          <FormattedMessage id="segmentInfo.class" />:
        </InfoGridHeaderCell>
        <InfoGridCell data-testid="class" column="span 3">
          {classOfService.name}
        </InfoGridCell>
        {!isAddOnLeg && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentInfo.trainNo" />:
            </InfoGridHeaderCell>
            <InfoGridCell data-testid="trainNo" column="span 3">
              {!!serviceType.code && `${serviceType.code} `}
              {trainNumber}
            </InfoGridCell>
          </>
        )}
        {passengers && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentInfo.pax" />:
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.passenger" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>{hasSeatingInfo() && <FormattedMessage id="reviewScreen.coach" />}</InfoGridHeaderCell>
            <InfoGridHeaderCell>{hasSeatingInfo() && <FormattedMessage id="reviewScreen.seat" />}</InfoGridHeaderCell>
            {passengers.map((pax) => (
              <Fragment key={pax.id}>
                <InfoGridCell />
                <InfoGridCell data-pax-id={pax.id} data-cy={pax.id} key={hash(pax)}>
                  {pax.firstName} {pax.lastName}
                </InfoGridCell>
                <InfoGridCell data-pax-coach={pax.id}>{hasSeatingInfo() && getCoach(pax)}</InfoGridCell>
                <InfoGridCell data-pax-seat={pax.id}>{hasSeatingInfo() && getSeat(pax)}</InfoGridCell>
              </Fragment>
            ))}
          </>
        )}
        {fareInfos && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id={fareInfos.label} />:
            </InfoGridHeaderCell>
            <InfoGridCell data-cy={fareInfos.dataId} data-testid={fareInfos.dataId} column="span 3">
              {fareInfos.value}
              {isMiceGroup ? ' (Mice Group)' : ''}
            </InfoGridCell>
          </>
        )}
        <PaxInfo
          paxDetails={groupPaxInfosByType}
          currency={firstBookingItem?.currency}
          isABSOrAdsAccessibleTravel={overrideAccesibleTravelPaxTypes}
        />
        {shouldShowMeals && (
          <>
            <InfoGridHeaderCell data-cy="meals">
              <FormattedMessage id="segmentInfo.meals" />:
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="paymentsSummary.amount" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.option" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.pricePerUnit" />
            </InfoGridHeaderCell>
            {mapObject(
              (key, num) => (
                <Fragment key={key}>
                  <InfoGridCell />
                  <InfoGridCell data-cy={`meals-${key}-${num}`}>{num}</InfoGridCell>
                  <InfoGridCell data-cy={`meals-${key}`}>{key}</InfoGridCell>
                  <InfoGridCell>
                    {currencies[firstBookingItem?.currency]}
                    {0}
                  </InfoGridCell>
                </Fragment>
              ),
              groupedMeals
            )}
          </>
        )}
        {!isAddOnLeg && extraProductsByPassengers.length > 0 && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentInfo.extras" />:
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="paymentsSummary.amount" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.item" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.pricePerUnit" />
            </InfoGridHeaderCell>
            {extraProductsByPassengers.map(({ product: { name, price }, quantity }) => (
              <Fragment key={name}>
                <InfoGridCell />
                <InfoGridCell>{quantity}</InfoGridCell>
                <InfoGridCell data-extra-name={`${name}`}>{name}</InfoGridCell>
                <InfoGridCell data-extra-price={`${name}`}>
                  {currencies[currency]}
                  {price}
                </InfoGridCell>
              </Fragment>
            ))}
          </>
        )}
        {firstBookingItem && serviceType.code === 'ES' && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentSummary.train.margin" />:
            </InfoGridHeaderCell>
            <InfoGridCell data-testid="price-margin-price" column="span 3">
              <FormattedCurrency currency={firstBookingItem?.currency} amount={margin || 0} />
            </InfoGridCell>
          </>
        )}
        <InfoGridHeaderCell>
          <FormattedMessage id="exchangeConfirmation.total" values={{ direction }} />:
        </InfoGridHeaderCell>
        <InfoGridCell data-cy={`total--${direction}`} data-testid={`price-total--${direction}`} column="span 3">
          <FormattedCurrency currency={firstBookingItem?.currency} amount={sum([total, extrasTotal])} />
          {!!totalPoints && <LoyaltyPointsInfos dataId="points-details" id="searchResults.pts" values={totalPoints} />}
        </InfoGridCell>
      </InfoGrid>

      {firstBookingItem && (
        <FareConditionsContainer>
          <Column>
            <InfoLabel>
              <FormattedMessage id="segmentSummary.fareConditions" />:
            </InfoLabel>
          </Column>
          <StyledFareCondition>
            <ul>
              <li>
                <InventoryClassMessage name={firstBookingItem.name} classOfService={classOfService?.name} />
              </li>
              <li>
                <RebookMessage
                  canBeRebooked={firstBookingItem.canBeRebooked}
                  afterSalesRules={firstBookingItem.afterSalesRules}
                  timezone={origin?.timezone}
                  currency={firstBookingItem.currency}
                />
              </li>
              <li>
                <RefundMessage
                  canLookupRefundMessage={canLookupMessage}
                  afterSalesRules={firstBookingItem.afterSalesRules}
                  timezone={origin?.timezone}
                  currency={currency}
                  canBeCancelled={canBeCancelled}
                />
              </li>
            </ul>
          </StyledFareCondition>
        </FareConditionsContainer>
      )}
    </>
  );
};

SegmentInfo.propTypes = {
  isAddOnLeg: PropTypes.bool,
  leg: legShape,
  intl: intlShape,
  currency: PropTypes.string,
  trainMarginDeprecated: PropTypes.number,
  isMiceGroup: PropTypes.bool
};

export default SegmentInfo;
